// DarkModeToggle — three-state theme switcher (Session 17 / C7).
//
// States: 'light' | 'dark' | 'system'. Preference is persisted in
// localStorage under 'nsofar.theme'; default is 'system' (follow the
// OS's prefers-color-scheme). Cycles light → dark → system on click;
// shift+click jumps straight to system.
//
// Renders on sm+ only — the mobile top bar (hamburger + currency +
// notifications + SOS) is already packed. Mobile users get system-
// default behaviour, which is what most expect anyway.
//
// The actual class-on-<html> application is done in two places:
//   1. A bootstrap script in public/index.html runs before React
//      mounts, applying the saved preference to avoid a flash of
//      the wrong theme.
//   2. The setTheme function below, called on every toggle click,
//      keeps the class in sync going forward.

import { useEffect, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';

const LS_KEY = 'nsofar.theme';

function getSystemPrefersDark() {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches || false;
  } catch {
    return false;
  }
}

function applyTheme(pref) {
  const isDark = pref === 'dark' || (pref === 'system' && getSystemPrefersDark());
  document.documentElement.classList.toggle('dark', isDark);
}

function readPref() {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch { /* private mode */ }
  return 'system';
}

function writePref(pref) {
  try { localStorage.setItem(LS_KEY, pref); } catch { /* private mode */ }
}

export default function DarkModeToggle() {
  const [pref, setPref] = useState(readPref);

  // When pref is 'system', track OS changes too.
  useEffect(() => {
    applyTheme(pref);
    if (pref !== 'system') return;
    let mq;
    try {
      mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      mq.addEventListener?.('change', handler);
      return () => mq.removeEventListener?.('change', handler);
    } catch {
      return undefined;
    }
  }, [pref]);

  const cycle = (e) => {
    if (e.shiftKey) {
      setPref('system');
      writePref('system');
      return;
    }
    const next = pref === 'light' ? 'dark' : pref === 'dark' ? 'system' : 'light';
    setPref(next);
    writePref(next);
  };

  const Icon = pref === 'light' ? Sun : pref === 'dark' ? Moon : Monitor;
  const label = pref === 'light' ? 'Light theme · click for dark'
              : pref === 'dark'  ? 'Dark theme · click for system'
              :                    'System theme · click for light';

  return (
    <button
      type="button"
      onClick={cycle}
      aria-label={label}
      title={label}
      className="hidden sm:inline-flex w-10 h-10 rounded-full items-center justify-center transition-colors hover:bg-[var(--hover-surface)]"
      data-testid="dark-mode-toggle"
      data-theme-pref={pref}
    >
      <Icon size={18} style={{ color: 'var(--text-muted)' }} />
    </button>
  );
}
